/**
 * Renderer UDV para CornerstoneTools ReferenceLines.
 *
 * El renderer stock de CT (planePlaneIntersection) recorta la recta al
 * rectángulo paciente del plano *reference*. Con FOV distinto (sag vs cor)
 * eso puede dibujar en un sentido y fallar en el otro — típico “sag sí / cor no”.
 *
 * Aquí proyectamos la intersección sobre el plano *target* (como el scout UDV)
 * y recortamos a la imagen visible. También toleramos FoR distinto si hay
 * geometría ortogonal usable (estudios multi-serie con FoR por serie).
 */
import { projectSliceLineOntoScout, extendLineToImageEdges } from './scoutReferenceLines'

function toArr3(v) {
  if (!v) return null
  if (Array.isArray(v) && v.length >= 3) {
    return [Number(v[0]), Number(v[1]), Number(v[2])]
  }
  if (typeof v === 'object' && v.x != null) {
    return [Number(v.x), Number(v.y), Number(v.z)]
  }
  return null
}

function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ]
}

function len3(a) {
  return Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2])
}

function normalize(a) {
  const L = len3(a)
  if (!(L > 1e-12)) return null
  return [a[0] / L, a[1] / L, a[2] / L]
}

function planeToScoutGeo(plane, image) {
  if (!plane) return null
  const row = toArr3(plane.rowCosines)
  const col = toArr3(plane.columnCosines)
  const ipp = toArr3(plane.imagePositionPatient)
  if (!row || !col || !ipp) return null
  const rows =
    Number(plane.rows) ||
    (image && image.rows) ||
    0
  const cols =
    Number(plane.columns) ||
    (image && image.columns) ||
    0
  if (!(rows > 0) || !(cols > 0)) return null
  const rowSp =
    Number(plane.rowPixelSpacing) ||
    (image && Number(image.rowPixelSpacing)) ||
    1
  const colSp =
    Number(plane.columnPixelSpacing) ||
    (image && Number(image.columnPixelSpacing)) ||
    rowSp
  return {
    ipp,
    iop: [...row, ...col],
    rows,
    cols,
    spacing: [rowSp, colSp],
  }
}

function normalsAngleRad(planeA, planeB) {
  const rA = toArr3(planeA.rowCosines)
  const cA = toArr3(planeA.columnCosines)
  const rB = toArr3(planeB.rowCosines)
  const cB = toArr3(planeB.columnCosines)
  if (!rA || !cA || !rB || !cB) return null
  const nA = normalize(cross(rA, cA))
  const nB = normalize(cross(rB, cB))
  if (!nA || !nB) return null
  const d = Math.abs(nA[0] * nB[0] + nA[1] * nB[1] + nA[2] * nB[2])
  return Math.acos(Math.min(1, Math.max(0, d)))
}

/**
 * @param {CanvasRenderingContext2D} context
 * @param {object} eventData
 * @param {HTMLElement} targetElement
 * @param {HTMLElement} referenceElement
 * @returns {boolean} true si dibujó
 */
export default function udvRenderActiveReferenceLine(
  context,
  eventData,
  targetElement,
  referenceElement
) {
  const cornerstone =
    (typeof window !== 'undefined' && window.cornerstone) || null
  const csTools =
    (typeof window !== 'undefined' && window.cornerstoneTools) || null
  if (!cornerstone || !targetElement || !referenceElement) return false

  let targetEnabled
  let referenceEnabled
  try {
    targetEnabled = cornerstone.getEnabledElement(targetElement)
    referenceEnabled = cornerstone.getEnabledElement(referenceElement)
  } catch (e) {
    return false
  }
  const targetImage = targetEnabled && targetEnabled.image
  const referenceImage = referenceEnabled && referenceEnabled.image
  if (!targetImage || !referenceImage) return false

  const targetPlane = cornerstone.metaData.get(
    'imagePlaneModule',
    targetImage.imageId
  )
  const referencePlane = cornerstone.metaData.get(
    'imagePlaneModule',
    referenceImage.imageId
  )
  if (!targetPlane || !referencePlane) return false

  const angle = normalsAngleRad(targetPlane, referencePlane)
  // Misma regla CT: ~30° (0.5 rad)
  if (angle == null || angle < 0.5) return false

  const targetGeo = planeToScoutGeo(targetPlane, targetImage)
  const refGeo = planeToScoutGeo(referencePlane, referenceImage)
  if (!targetGeo || !refGeo) return false

  const line = projectSliceLineOntoScout(targetGeo, refGeo, 0)
  if (!line) return false

  // Forzar borde a borde del matrix (no FOV parcial del otro stack)
  const full =
    extendLineToImageEdges(
      line.x1,
      line.y1,
      line.x2,
      line.y2,
      targetGeo.cols,
      targetGeo.rows
    ) || line

  const color =
    (csTools &&
      csTools.toolColors &&
      typeof csTools.toolColors.getActiveColor === 'function' &&
      csTools.toolColors.getActiveColor()) ||
    '#76FF03'

  const start = { x: full.x1, y: full.y1 }
  const end = { x: full.x2, y: full.y2 }

  const drawApi = csTools && (csTools.drawing || csTools.importInternal)
  const drawFn =
    (csTools && csTools.draw) ||
    (drawApi && drawApi.draw) ||
    null
  const drawLineFn =
    (csTools && csTools.drawLine) ||
    (drawApi && drawApi.drawLine) ||
    null

  try {
    if (typeof drawFn === 'function' && typeof drawLineFn === 'function') {
      context.setTransform(1, 0, 0, 1, 0, 0)
      drawFn(context, (ctx) => {
        drawLineFn(ctx, eventData.element, start, end, { color })
      })
      return true
    }
  } catch (e) {
    /* fall through */
  }

  // Fallback: renderToolData ya dejó el context en pixel space
  try {
    const prev = context.strokeStyle
    const prevW = context.lineWidth
    context.beginPath()
    context.strokeStyle = color
    context.lineWidth = 2 / (eventData && eventData.viewport && eventData.viewport.scale
      ? eventData.viewport.scale
      : 1)
    context.moveTo(start.x, start.y)
    context.lineTo(end.x, end.y)
    context.stroke()
    context.strokeStyle = prev
    context.lineWidth = prevW
    return true
  } catch (e2) {
    return false
  }
}
